/**
 * PropertyGrid – mobile card view for property list
 */
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import type { Property, PropertyGridProps } from '../types'
import { useUserRole } from '@/hooks/useUserRole'
import { formatPafSummaryDate } from '@/features/company/utils/pafDateFormat'

function formatAddress(p: Property): string {
  const parts = [
    p.property_address,
    p.property_address2,
    [p.property_city, p.property_state, p.property_zipcode].filter(Boolean).join(', '),
  ].filter(Boolean)
  return parts.join(' ')
}

export function PropertyGrid({
  properties,
  onToggleListed,
  loading = false,
}: PropertyGridProps) {
  const { isLibertyRole, hasPolicy, hasWriteAccess } = useUserRole()
  const canEditListed =
    hasPolicy(['property_listed_edit']) && hasWriteAccess('property')

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        data-testid="property-list-loading"
      >
        <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" />
      </div>
    )
  }

  if (properties.length === 0) {
    return (
      <div
        data-testid="property-list-empty"
        className="p-12 text-center text-slate-500"
      >
        No properties found.
      </div>
    )
  }

  return (
    <div data-testid="property-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-4">
      {properties.map((property) => (
        <div
          key={property.property_id}
          data-testid={`property-grid-card-${property.property_id}`}
          className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
        >
          <div className="p-3 sm:p-4 space-y-2.5 sm:space-y-3">
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                Property Name
              </p>
              <Link
                data-testid={`property-grid-card-${property.property_id}-name-link`}
                to={`/property/edit/${property.property_id}`}
                className="text-[rgb(21_151_255)] hover:text-[#0d7acc] font-semibold text-sm hover:underline block"
              >
                {property.property_name}
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">ID</p>
                <p className="text-sm text-slate-700">{property.property_id}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Company</p>
                <p className="text-sm text-slate-700">{property.company_name ?? '—'}</p>
              </div>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Address</p>
              <p className="text-sm text-slate-700 truncate" title={formatAddress(property)}>
                {formatAddress(property) || '—'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Manager</p>
                <p className="text-sm text-slate-700">{property.contact_name ?? '—'}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Units</p>
                <p className="text-sm text-slate-700">{property.units_count ?? '—'}</p>
              </div>
            </div>
            {isLibertyRole && (
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Alias</p>
                <p className="text-sm text-slate-700">{property.property_alias_name ?? '—'}</p>
              </div>
            )}
            {property.deleted_at && isLibertyRole && (
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Deleted</p>
                <p className="text-sm text-slate-700 whitespace-nowrap">
                  {formatPafSummaryDate(property.deleted_at) || '—'}
                </p>
              </div>
            )}
          </div>
          <div className="border-t border-slate-100 px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between bg-slate-50/50 flex-wrap gap-2">
            <button
              data-testid={`property-grid-card-${property.property_id}-listed-button`}
              onClick={() => onToggleListed?.(property)}
              disabled={!canEditListed}
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                property.org_listed_ids?.['1'] === 1
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-red-100 text-red-700'
              } ${!canEditListed ? 'opacity-60 cursor-not-allowed' : ''}`}
              type="button"
            >
              {property.org_listed_ids?.['1'] === 1 ? 'Active' : 'Not Active'}
            </button>
            <Link
              data-testid={`property-grid-card-${property.property_id}-view-link`}
              to={`/property/edit/${property.property_id}`}
              className={cn(primaryEntityActionButtonClass, 'h-8 min-w-0 px-3 text-xs')}
            >
              View
            </Link>
          </div>
        </div>
      ))}
    </div>
  )
}
